/**
 * Stripe Composable
 * Client-side utilities for Stripe integration
 */

import { logApiError } from '~/utils/logger'

interface CheckoutSessionResponse {
  success: boolean
  message?: string
  data?: {
    session_id: string
    checkout_url: string | null
  }
}

interface PaymentIntentResponse {
  success: boolean
  message?: string
  data?: {
    client_secret: string
    payment_intent_id: string
    amount: number
  }
}

export function useStripe() {
  /**
   * Create a checkout session and redirect to Stripe (legacy flow)
   */
  const createCheckoutSession = async (bookingId: string) => {
    try {
      const response = await $fetch<CheckoutSessionResponse>('/api/payments/create-checkout-session', {
        method: 'POST',
        body: {
          booking_id: bookingId,
        },
      })

      if (!response.success || !response.data?.checkout_url)
        throw new Error(response.message || 'Failed to create checkout session')

      return response.data
    }
    catch (error) {
      logApiError('/api/payments/create-checkout-session', error, { bookingId })
      throw error
    }
  }

  /**
   * Create a PaymentIntent for embedded payment element
   */
  const createPaymentIntent = async (bookingId: string) => {
    try {
      const response = await $fetch<PaymentIntentResponse>('/api/payments/create-payment-intent', {
        method: 'POST',
        body: {
          booking_id: bookingId,
        },
      })

      if (!response.success || !response.data?.client_secret)
        throw new Error(response.message || 'Failed to create payment intent')

      return response.data
    }
    catch (error) {
      logApiError('/api/payments/create-payment-intent', error, { bookingId })
      throw error
    }
  }

  /**
   * Redirect to Stripe checkout (legacy flow)
   */
  const redirectToCheckout = async (bookingId: string) => {
    const session = await createCheckoutSession(bookingId)

    if (session.checkout_url)
      window.location.href = session.checkout_url
  }

  return {
    createCheckoutSession,
    createPaymentIntent,
    redirectToCheckout,
  }
}
